import { useModal } from '@circlefin/modal-router'
import { useTravelRuleRequired } from '@features/users/hooks/travel-rule'
import { routes } from '@services/sections/modal'
import {
  ADDRESS_IS_NOT_ELIGIBLE,
  ADDRESS_IS_NOT_TRM_SUPPORTED,
  OWNER_TYPE_NOT_ALLOWED,
  getError,
} from '@shared/apollo/lib/error/codes'
import {
  BlockchainRecipientAddressesWithMetadataDocument,
  BlockchainRecipientAddressOwner,
  RecipientAddressFilterBy,
  useCreateBlockchainRecipientAddressWithMetadataMutation,
} from '@shared/graphql'
import { useMfa } from '@shared/mfa'

import { getAddAddressModalPathByStatus } from '../../utils'

import type { ApolloError } from '@apollo/client'

/**
 * Hook to add a recipient blockchain address.
 */
export const useAddRecipientBlockchainAddress = ({
  isCustomer = true,
}: {
  isCustomer?: boolean
} = {}) => {
  const modal = useModal()
  const [, { mfaError }] = useMfa()
  const { travelRuleRequired, travelRuleFranceRequired } =
    useTravelRuleRequired()

  const [addRecipientBlockchainAddress, { loading }] =
    useCreateBlockchainRecipientAddressWithMetadataMutation({
      onCompleted: ({ createBlockchainRecipientAddressWithMetadata }) => {
        if (
          !createBlockchainRecipientAddressWithMetadata.requiredApproval &&
          createBlockchainRecipientAddressWithMetadata.response != null
        ) {
          return modal.router.push({
            pathname: getAddAddressModalPathByStatus(
              createBlockchainRecipientAddressWithMetadata.response.status,
            ),
            query: {
              addressId:
                createBlockchainRecipientAddressWithMetadata.response.id,
              owner:
                createBlockchainRecipientAddressWithMetadata.response.owner,
            },
          })
        }

        modal.router.push(
          routes.account.addressBook.addNewAddress.approvalRequired,
        )
      },
      onError: (error) => {
        if (
          isKnownError(isCustomer, error, {
            travelRuleRequired,
            travelRuleFranceRequired,
          })
        ) {
          return modal.router.push(
            routes.account.addressBook.addNewAddress.error,
          )
        }

        mfaError({ error })
      },
      refetchQueries: [
        {
          query: BlockchainRecipientAddressesWithMetadataDocument,
          variables: {
            filter: {
              owner: isCustomer
                ? BlockchainRecipientAddressOwner.customer
                : BlockchainRecipientAddressOwner.non_customer_entity,
              show: RecipientAddressFilterBy.ALL_ADDRESSES,
            },
          },
        },
      ],
      awaitRefetchQueries: true,
    })

  return [addRecipientBlockchainAddress, { loading }] as const
}

/**
 * Check if the error is a known error.
 */
const isKnownError = (
  isCustomer: boolean,
  error: ApolloError,
  {
    travelRuleRequired,
    travelRuleFranceRequired,
  }: {
    travelRuleRequired: boolean
    travelRuleFranceRequired: boolean
  },
) => {
  if (isCustomer) {
    return travelRuleRequired && isKnownTravelRuleError(error)
  }

  return travelRuleFranceRequired && isKnownTravelRuleFranceError(error)
}

/**
 * Check if the error is a known travel rule error.
 */
const isKnownTravelRuleError = (error: ApolloError) => {
  const { graphQLErrors } = error

  return Boolean(
    getError(OWNER_TYPE_NOT_ALLOWED, graphQLErrors) ??
      getError(ADDRESS_IS_NOT_ELIGIBLE, graphQLErrors) ??
      getError(ADDRESS_IS_NOT_TRM_SUPPORTED, graphQLErrors),
  )
}

/**
 * Check if the error is a known travel rule France error.
 */
const isKnownTravelRuleFranceError = (error: ApolloError) => {
  const { graphQLErrors } = error

  return Boolean(getError(ADDRESS_IS_NOT_ELIGIBLE, graphQLErrors))
}
